import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import '../components/styles.css';
import './BeritaPage.css'; // Import the new CSS file
import AnimatedSection from '../components/AnimatedSection';
import { db } from '../firebase'; // Pastikan path ini benar
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore'; // Pastikan import ini benar
import SEO from '../components/SEO';

// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/pagination';

const getCategoryColor = (categoryName) => {
  const colors = ['#e74c3c','#3498db','#2ecc71','#f39c12','#9b59b6','#1abc9c','#e67e22','#34495e', '#d35400', '#c0392b'];
  if (!categoryName) return colors[0];
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const BeritaPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryColors, setCategoryColors] = useState({});
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('Semua');
  const [activeCategory, setActiveCategory] = useState('Semua');
  const [allBerita, setAllBerita] = useState([]);
  const [featuredBerita, setFeaturedBerita] = useState([]); // State for slider

  const [filteredBerita, setFilteredBerita] = useState([]);
  const [visibleCount, setVisibleCount] = useState(6);
  const [loading, setLoading] = useState(true);

  // Reset visibleCount when filter changes
  useEffect(() => {
    setVisibleCount(6);
  }, [searchTerm, activeCategory, selectedYear]);

  useEffect(() => {
    const fetchBerita = async () => {
      setLoading(true);
      // Ambil Kategori dan Warna
      const categoriesQuery = query(collection(db, "berita_kategori"), orderBy("name", "asc"));
      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categoriesList = [];
      const colorsMap = {};
      categoriesSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.name) {
          categoriesList.push(data.name);
          if (data.color) {
            colorsMap[data.name] = data.color;
          }
        }
      });
      setCategories(['Semua', ...categoriesList]);
      setCategoryColors(colorsMap);

      // Ambil Berita
      const beritaQuery = query(collection(db, "berita"), where("status", "==", "published"), orderBy("date", "desc"));
      const beritaSnapshot = await getDocs(beritaQuery);
      const beritaList = beritaSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Ekstrak tahun unik dari berita
      const yearSet = new Set();
      beritaList.forEach(item => {
        const date = item.date && item.date.toDate ? item.date.toDate() : new Date(item.date);
        if (!isNaN(date.getTime())) {
          yearSet.add(date.getFullYear());
        }
      });
      const sortedYears = Array.from(yearSet).sort((a, b) => b - a);
      setYears(['Semua', ...sortedYears]);

      setAllBerita(beritaList);

      // Split for Featured Slider (Top 5) and Regular List
      setFeaturedBerita(beritaList.slice(0, 5));
      setFilteredBerita(beritaList); // Show all news in the list


      setLoading(false);
    };

    fetchBerita();
  }, []);

  useEffect(() => {
    // If search or filter is active, look through ALL berita. 
    // If not, show regular berita (because top 5 are in slider).
    let results = allBerita; // Always filter from the full list


    // Filter berdasarkan kategori
    if (activeCategory !== 'Semua') {
      results = results.filter(berita => berita.category === activeCategory);
    }
    // Filter berdasarkan tahun
    if (selectedYear !== 'Semua') {
      results = results.filter(berita => {
        const date = berita.date && berita.date.toDate ? berita.date.toDate() : new Date(berita.date);
        return date.getFullYear().toString() === selectedYear.toString();
      });
    }
    // Filter berdasarkan pencarian
    results = results.filter(berita => berita.title.toLowerCase().includes(searchTerm.toLowerCase()));
    setFilteredBerita(results);
  }, [searchTerm, activeCategory, selectedYear, allBerita]);

  const currentItems = filteredBerita.slice(0, visibleCount);
  const observer = useRef();
  
  const lastBeritaElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleCount < filteredBerita.length) {
        setVisibleCount(prevVisibleCount => prevVisibleCount + 6);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, visibleCount, filteredBerita.length]);

  return (
    <AnimatedSection>
      <SEO
        title="Berita & Kegiatan - SMK Pariwisata Sumbangsih"
        description="Informasi terbaru seputar kegiatan sekolah, prestasi siswa, dan pengumuman penting dari SMK Pariwisata Sumbangsih."
      />
      <section id="berita-page" className="section" style={{ paddingTop: '40px' }}>
        <div className="container">

          {/* Featured News Slider */}
          {!loading && featuredBerita.length > 0 && !searchTerm && activeCategory === 'Semua' && selectedYear === 'Semua' && (
            <div className="berita-hero-section">
              <h2 className="berita-section-title">Berita Unggulan</h2>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                loop={true}
                className="berita-hero-slider"
              >
                {featuredBerita.map((item) => (
                  <SwiperSlide key={item.id}>
                    <div className="hero-slide-content">
                      <div className="hero-slide-blur-bg" style={{ backgroundImage: `url(${item.image})` }}></div>
                      <img src={item.image} alt={item.title} className="hero-slide-image" />
                      <div className="hero-slide-overlay">
                        <div className="hero-slide-text">
                          <span className="hero-slide-category" style={{ backgroundColor: categoryColors[item.category] || getCategoryColor(item.category || 'Berita') }}>{item.category || 'Berita'}</span>
                          <h3>{item.title}</h3>
                          <p>{new Date(item.date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                          <Link to={`/berita/${item.slug}`} className="btn-hero-slide">Baca Selengkapnya</Link>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          <div id="berita-list-start" className="berita-list-header">
            <h2>Berita Terbaru</h2>
          </div>

          <div className="berita-sticky-header" style={{ position: 'static', zIndex: 'auto' }}>
            <div className="berita-filter-search-wrapper">
              <div className="search-bar-container" style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  placeholder="Cari berita..."
                  className="search-bar"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="year-filter-container">
                <select
                  className="year-filter-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {years.map(year => (
                    <option key={year} value={year}>
                      {year === 'Semua' ? 'Semua Tahun' : year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Swiper
              modules={[Navigation]}
              spaceBetween={10}
              slidesPerView={'auto'}
              navigation
              className="category-filters-slider"
            >
              {categories.map(category => (
                <SwiperSlide key={category} style={{ width: 'auto' }}>
                  <button
                    className={`category-btn ${activeCategory === category ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category)}
                  >
                    {category}
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {loading ? (
            <div className="berita-list">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="berita-card skeleton">
                  <div className="berita-card-image"></div>
                  <div className="berita-card-content">
                    <span className="berita-card-date" aria-hidden="true"></span>
                    <h3 className="berita-card-title" aria-hidden="true"></h3>
                    <p className="berita-card-excerpt" aria-hidden="true"></p>
                    <span className="btn-berita-selengkapnya" aria-hidden="true"></span>
                  </div>
                </div>
              ))}
            </div>
          ) : currentItems.length > 0 ? (
            <>
              <div className="berita-list">
                {currentItems.map((berita, index) => {
                  const isLastElement = index === currentItems.length - 1;
                  return (
                    <Link 
                      ref={isLastElement ? lastBeritaElementRef : null}
                      to={`/berita/${berita.slug}`} 
                      key={berita.id} 
                      className="berita-card"
                    >
                      <img src={berita.image} alt={berita.title} className="berita-card-image" />
                      {berita.category && (
                        <span className="berita-card-category" style={{ backgroundColor: categoryColors[berita.category] || getCategoryColor(berita.category) }}>{berita.category}</span>
                      )}
                      <div className="berita-card-content">
                        <span className="berita-card-date">{new Date(berita.date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        <h3 className="berita-card-title">{berita.title}</h3>
                        <p className="berita-card-excerpt">{berita.excerpt}</p>
                        <span className="btn-berita-selengkapnya">Baca Selengkapnya <span className="arrow-icon">&rarr;</span></span>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {visibleCount < filteredBerita.length && (
                <div style={{ textAlign: 'center', marginTop: '20px', padding: '20px' }}>
                  <div className="loader" style={{ display: 'inline-block', width: '30px', height: '30px', border: '3px solid rgba(0,0,0,0.1)', borderRadius: '50%', borderTopColor: 'var(--primary-color)', animation: 'spin 1s ease-in-out infinite' }}></div>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              )}
            </>
          ) : (
            <div className="no-results">
              <p>Tidak ada berita yang cocok dengan pencarian Anda.</p>
            </div>
          )}
        </div>
      </section>
    </AnimatedSection>
  );
};

export default BeritaPage;